import { useMemo } from 'react';
import type {
  HouseRules,
  RoundDirection,
  ScoringConfig,
  ScoringPresetId,
} from '@shared/types/game';
import {
  buildRoundPlan,
  deckLimitFor,
  estimateMinutes,
  maxCardsFor,
  totalTricks,
} from '@shared/game-logic';
import { Button, Hint, Panel, Segmented, Toggle } from '@/components/ui';
import {
  DIRECTION_LABELS,
  SCORING_PRESET_LABELS,
  SCORING_PRESET_SUMMARIES,
  formatMinutes,
} from '@/lib/labels';

/**
 * Rounds, scoring and house rules — shared by the scorekeeper's new-game screen
 * and the live-room screen.
 *
 * One component on purpose. These are the actual rules of the game, and two
 * copies of this UI would eventually disagree about what the table plays; that
 * bug already happened once, when a live room silently used the server defaults
 * because it had no settings screen at all.
 */

export interface GameSettingsValue {
  maxCards: number;
  direction: RoundDirection;
  repeatTopRound: boolean;
  extraNoTrumpRound: boolean;
  noTrumpOnTheWayDown: boolean;
  scoringPreset: ScoringPresetId;
  customScoring: ScoringConfig;
  houseRules: HouseRules;
}

/**
 * The scorekeeper knows who is at the table, so it can show the exact deck
 * limit. A live room is created before anyone joins, so the limit is unknown
 * and the engine clamps it when the host starts.
 */
export type TableSize = { known: true; playerCount: number } | { known: false };

/** Highest hand size worth offering when the table size is not known yet. */
const UNKNOWN_TABLE_CAP = 13;

export default function GameSettings({
  value,
  onChange,
  table,
}: {
  value: GameSettingsValue;
  onChange: (patch: Partial<GameSettingsValue>) => void;
  table: TableSize;
}) {
  const deckLimit = table.known ? deckLimitFor(table.playerCount) : UNKNOWN_TABLE_CAP;
  const effectiveMax = table.known
    ? maxCardsFor(table.playerCount, value.maxCards)
    : Math.min(value.maxCards, UNKNOWN_TABLE_CAP);

  // Only previewable once the table size is known; a live room gets a note instead.
  const plan = useMemo(() => {
    if (!table.known) return null;
    return buildRoundPlan({
      playerCount: table.playerCount,
      maxCards: value.maxCards,
      direction: value.direction,
      repeatTopRound: value.repeatTopRound,
      extraNoTrumpRound: value.extraNoTrumpRound,
      noTrumpOnTheWayDown: value.noTrumpOnTheWayDown,
      firstDealerSeat: 0,
    });
  }, [
    table,
    value.maxCards,
    value.direction,
    value.repeatTopRound,
    value.extraNoTrumpRound,
    value.noTrumpOnTheWayDown,
  ]);

  return (
    <>
      <Panel title="Rondes">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Grootste ronde</p>
            <p className="text-sm text-slate-400">
              {table.known
                ? `Met ${table.playerCount} spelers past er maximaal ${deckLimit}`
                : 'Bij meer spelers worden er automatisch minder kaarten gedeeld'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Minder kaarten"
              onClick={() => onChange({ maxCards: Math.max(1, value.maxCards - 1) })}
              className="h-11 w-11 rounded-xl bg-base-700 text-lg"
            >
              −
            </button>
            <span className="w-8 text-center text-xl font-semibold tabular-nums">
              {effectiveMax}
            </span>
            <button
              type="button"
              aria-label="Meer kaarten"
              onClick={() => onChange({ maxCards: Math.min(deckLimit, value.maxCards + 1) })}
              className="h-11 w-11 rounded-xl bg-base-700 text-lg"
              disabled={effectiveMax >= deckLimit}
            >
              +
            </button>
          </div>
        </div>

        {/* With three players the deck allows 17, which is a 33-round marathon —
            so the one-tap option only appears once the table is big enough, and
            for an unknown table it offers the 4-player deck. */}
        {(!table.known || table.playerCount >= 4) && (
          <div className="mt-3">
            <Button
              full
              onClick={() => onChange({ maxCards: deckLimit })}
              disabled={effectiveMax === deckLimit}
            >
              {effectiveMax === deckLimit
                ? `Heel deck (${deckLimit} kaarten) ✓`
                : `Heel deck gebruiken — ${deckLimit} kaarten`}
            </Button>
          </div>
        )}

        <div className="mt-4">
          <Segmented
            value={value.direction}
            onChange={(direction) => onChange({ direction })}
            options={[
              { value: 'up-down', label: DIRECTION_LABELS['up-down'] },
              { value: 'up', label: DIRECTION_LABELS.up },
              { value: 'down', label: DIRECTION_LABELS.down },
            ]}
          />
        </div>

        {value.direction === 'up-down' && (
          <div className="mt-2">
            <Toggle
              checked={value.repeatTopRound}
              onChange={(repeatTopRound) => onChange({ repeatTopRound })}
              label="Grootste ronde twee keer"
              hint="Sommige tafels spelen de hoogste ronde dubbel voordat het weer omlaag gaat."
            />
          </div>
        )}

        <Toggle
          checked={value.extraNoTrumpRound}
          onChange={(extraNoTrumpRound) => onChange({ extraNoTrumpRound })}
          label="Extra ronde zonder troef"
          hint="Na de grootste ronde volgt nog een ronde met evenveel kaarten, maar troefloos."
        />

        {/* Only meaningful on the up-down ladder: a one-way ladder has no
            distinct way down, so this would turn every round trumpless. */}
        {value.direction === 'up-down' && (
          <Toggle
            checked={value.noTrumpOnTheWayDown}
            onChange={(noTrumpOnTheWayDown) => onChange({ noTrumpOnTheWayDown })}
            label="Hele terugweg zonder troef"
            hint="Omhoog met troef, omlaag alles troefloos. De tweede helft wordt daarmee flink zwaarder."
          />
        )}

        {plan && <LadderPreview plan={plan} />}
        {!table.known && (
          <p className="mt-3 text-sm text-slate-500">
            Zodra iedereen binnen is wordt het rondeschema bepaald op basis van het aantal spelers.
          </p>
        )}
      </Panel>

      <Panel title="Puntentelling">
        <Segmented
          value={value.scoringPreset}
          onChange={(scoringPreset) => onChange({ scoringPreset })}
          options={(
            ['nl-standaard', 'dubbel-bod', 'kwadraat', 'mild', 'eigen'] as ScoringPresetId[]
          ).map((preset) => ({
            value: preset,
            label: SCORING_PRESET_LABELS[preset],
            hint: SCORING_PRESET_SUMMARIES[preset],
          }))}
        />

        {value.scoringPreset === 'eigen' && (
          <div className="mt-3 space-y-3">
            <NumberField
              label="Punten bij precies goed"
              value={value.customScoring.exactBase}
              onChange={(exactBase) =>
                onChange({ customScoring: { ...value.customScoring, exactBase } })
              }
            />
            <NumberField
              label="Extra per geboden slag"
              value={value.customScoring.perTrickBid}
              onChange={(perTrickBid) =>
                onChange({ customScoring: { ...value.customScoring, perTrickBid } })
              }
            />
            <NumberField
              label="Extra per bod in het kwadraat"
              value={value.customScoring.bidSquaredBonus ?? 0}
              onChange={(bidSquaredBonus) =>
                onChange({ customScoring: { ...value.customScoring, bidSquaredBonus } })
              }
            />
            <NumberField
              label="Strafpunten per slag verschil"
              value={value.customScoring.missPenaltyPerTrick}
              onChange={(missPenaltyPerTrick) =>
                onChange({ customScoring: { ...value.customScoring, missPenaltyPerTrick } })
              }
            />
            <Toggle
              checked={value.customScoring.alsoScoreTricksOnMiss}
              onChange={(alsoScoreTricksOnMiss) =>
                onChange({ customScoring: { ...value.customScoring, alsoScoreTricksOnMiss } })
              }
              label="Bij fout tóch een punt per gehaalde slag"
            />
          </div>
        )}

        <p className="mt-3 text-sm text-slate-500">
          De telling wordt in dit potje opgeslagen. Pas je later de huisregels aan, dan blijft de
          stand van oude potjes ongewijzigd.
        </p>
      </Panel>

      <Panel title="Huisregels">
        <Toggle
          checked={value.houseRules.dealerRestriction}
          onChange={(dealerRestriction) =>
            onChange({ houseRules: { ...value.houseRules, dealerRestriction } })
          }
          label="De deler mag niet passend bieden"
          hint="De deler biedt als laatste en mag het totaal niet precies op het aantal slagen laten uitkomen."
        />
        <Toggle
          checked={value.houseRules.mustPlayTrumpIfVoid}
          onChange={(mustPlayTrumpIfVoid) =>
            onChange({ houseRules: { ...value.houseRules, mustPlayTrumpIfVoid } })
          }
          label="Troefplicht"
          hint="Kun je de gevraagde kleur niet volgen, dan moet je troeven als je troef hebt."
        />
        <Toggle
          checked={value.houseRules.blindSingleCardRound}
          onChange={(blindSingleCardRound) =>
            onChange({ houseRules: { ...value.houseRules, blindSingleCardRound } })
          }
          label="Blinde ronde bij één kaart"
          hint="Je houdt je enige kaart naar buiten: je ziet die van alle anderen, maar niet je eigen. Geldt in het live spel; met echte kaarten regel je het zelf."
        />
        <Hint title="Waarom mag de deler niet passend bieden?">
          Als de biedingen precies opgeteld het aantal slagen zijn, zou in theorie iedereen zijn bod
          kunnen halen. Door dat te verbieden zit er altijd minstens één speler fout — dat is
          precies de spanning van boerenbridge.
        </Hint>
      </Panel>
    </>
  );
}

function LadderPreview({ plan }: { plan: ReturnType<typeof buildRoundPlan> }) {
  return (
    <div className="mt-3 rounded-xl bg-base-900/60 p-3">
      <p className="text-sm">
        <span className="font-semibold">{plan.length} rondes</span>, {totalTricks(plan)} slagen,
        ongeveer {formatMinutes(estimateMinutes(plan))}
      </p>
      <p className="mt-1 flex flex-wrap gap-x-1.5 font-tabular text-xs text-slate-500">
        {plan.map((round, index) => (
          <span key={round.roundNo}>
            <span className={round.noTrump ? 'font-semibold text-accent-400' : ''}>
              {round.cards}
              {round.noTrump && 'Ø'}
            </span>
            {index < plan.length - 1 && <span className="text-slate-700"> ·</span>}
          </span>
        ))}
      </p>
      {plan.some((round) => round.noTrump) && (
        <p className="mt-1 text-xs text-slate-500">
          <span className="text-accent-400">Ø</span> = troefloze ronde
        </p>
      )}
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-4">
      <span className="text-sm">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        value={value}
        onChange={(event) => {
          const parsed = Number.parseInt(event.target.value, 10);
          onChange(Number.isNaN(parsed) ? 0 : parsed);
        }}
        className="min-h-11 w-20 rounded-xl border border-white/10 bg-base-900 px-3 text-right tabular-nums outline-none focus:border-accent-500"
      />
    </label>
  );
}

/**
 * One line saying what a room actually is.
 *
 * Lives here rather than on the screen that creates a room, because the lobby
 * has to say the same thing to everyone who joins — and two descriptions of one
 * set of rules drift apart the first time an option is added.
 *
 * Takes the parts it needs rather than a whole `GameSettingsValue`, so the live
 * game can hand it a `GameConfig` straight off the wire.
 */
export function describeSettings(rules: {
  maxCards: number;
  direction: RoundDirection;
  extraNoTrumpRound?: boolean;
  noTrumpOnTheWayDown?: boolean;
  repeatTopRound?: boolean;
  scoringPreset?: ScoringPresetId;
  houseRules?: Partial<HouseRules>;
}): string {
  const parts = [
    `tot ${rules.maxCards} kaarten`,
    rules.direction === 'up-down'
      ? 'op en neer'
      : rules.direction === 'up'
        ? 'oplopend'
        : 'aflopend',
  ];

  if (rules.repeatTopRound && rules.direction === 'up-down') parts.push('hoogste ronde dubbel');

  if (rules.noTrumpOnTheWayDown && rules.direction === 'up-down') {
    parts.push('terugweg troefloos');
  } else if (rules.extraNoTrumpRound) {
    parts.push('extra ronde troefloos');
  }

  if (rules.scoringPreset && rules.scoringPreset !== 'nl-standaard') {
    parts.push(`telling: ${SCORING_PRESET_LABELS[rules.scoringPreset]}`);
  }

  /*
   * House rules only when they are *not* the default. In the lobby this list is
   * read by someone about to tap "klaar", so it has to be short enough to read —
   * mentioning every rule that is simply normal would bury the one that is not.
   */
  const house = rules.houseRules;
  if (house) {
    if (house.dealerRestriction === false) parts.push('deler mag alles bieden');
    if (house.mustPlayTrumpIfVoid) parts.push('troefplicht');
    if (house.hiddenBidding) parts.push('verborgen bieden');
    if (house.blindSingleCardRound === false) parts.push('geen blinde ronde');
  }

  return parts.join(' · ');
}
